import React, { useState, useEffect, Suspense } from 'react';
import PropTypes from 'prop-types';


/**
 * Provides a Suspense wrapper for lazy-loading components on the client side only.
 * With our static site generation, the fallback will be used on the server.
 * @param {React.ElementType} Component - lazy-loaded component using React.lazy()
 * @param {React.ElementType} [FallbackComponent='span'] - optionally receives className as a prop
 * @returns {JSX.Element}
 */
function withSuspense(
    Component,
    FallbackComponent = 'span',
) {
    function LazyComponentWrapper({
        className,
        ...otherComponentProps
    }) {
        const [ showFallback, setShowFallback ] = useState(true);
        // We only want to render our lazy Component wrapped with Suspense on the client side.
        // This is due to Suspense’s interaction with our static site generation
        // it’s expecting a server to respond, but we don’t have that.
        useEffect(() => {
            setShowFallback(false);
        }, []);


        const fallbackElement = (
            <FallbackComponent
                {...otherComponentProps}
                className={className}
            />
        );

        return (
            <Choose>
                <When condition={showFallback}>
                    {fallbackElement}
                </When>
                <Otherwise>
                    <Suspense fallback={fallbackElement}>
                        <Component
                            className={className}
                            {...otherComponentProps}
                        />
                    </Suspense>
                </Otherwise>
            </Choose>
        );
    }

    LazyComponentWrapper.propTypes = {
        className: PropTypes.string,
    };

    return LazyComponentWrapper;
}

export default withSuspense;
